// Decision 79: "Review & merge" duplicate contacts. One pair at a time: pick the record to keep, read
// what will happen in plain words, then Merge. The page does the merge with Undo (nothing is sent
// until the Undo window has passed), so this screen only decides.
import React, { useEffect, useState } from 'react';
import { Modal, Button } from '../../ui';

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// What the merged-away record holds, as the server counted it, in words: "2 invoices, 1 proposal".
export function holdsText(h = {}) {
  const parts = [];
  if (h.invoices) parts.push(plural(h.invoices, 'invoice'));
  if (h.proposals) parts.push(plural(h.proposals, 'proposal'));
  if (h.contracts) parts.push(plural(h.contracts, 'contract'));
  if (h.bookings) parts.push(plural(h.bookings, 'booking'));
  if (h.history) parts.push(plural(h.history, 'history entry', 'history entries'));
  return parts.join(', ');
}

// The sentences shown above Merge. Exported for the test: they are the confirmation.
export function mergeSummary(keepIn, otherIn) {
  // Same name on both (the usual duplicate): say which is which by address, or the sentences read
  // "Ada's invoices move to Ada".
  const same = String(keepIn.name || '').trim().toLowerCase() === String(otherIn.name || '').trim().toLowerCase();
  const label = (c) => (same && c.email ? `${c.name} (${c.email})` : c.name);
  const keep = { ...keepIn, name: label(keepIn) };
  const other = { ...otherIn, name: label(otherIn) };
  const lines = [];
  const holds = holdsText(other.holds);
  lines.push(holds
    ? `${other.name}'s ${holds}, and anything else on that record, move to ${keep.name}.`
    : `Anything on ${other.name}'s record moves to ${keep.name}.`);
  const addresses = [other.email, ...(other.emailAliases || [])].filter(Boolean)
    .filter((a) => a.toLowerCase() !== String(keep.email || '').toLowerCase());
  if (addresses.length) lines.push(`${addresses.join(' and ')} ${addresses.length === 1 ? 'is' : 'are'} kept as another address for ${keep.name}, so mail and bookings from ${addresses.length === 1 ? 'it' : 'them'} still find this client.`);
  if (other.holds?.portal && keep.holds?.portal) lines.push(`Both have a client portal. ${other.name}'s messages and files move into ${keep.name}'s portal, and ${other.name}'s portal link stops working.`);
  else if (other.holds?.portal) lines.push(`${other.name}'s client portal, and its link, now belong to ${keep.name}.`);
  lines.push(`${other.name} is then removed from Contacts. You can undo for a few seconds.`);
  return lines;
}

function Side({ c, chosen, onChoose }) {
  return (
    <label className={`contacts-merge__side${chosen ? ' is-chosen' : ''}`}>
      <input type="radio" name="merge-keep" checked={chosen} onChange={onChoose} aria-label={`Keep ${c.name}`} />
      <span className="contacts-merge__keep">{chosen ? 'Keep this one' : 'Merge into the other'}</span>
      <strong className="contacts-merge__name">{c.name}</strong>
      {c.email && <span>{c.email}</span>}
      {(c.emailAliases || []).map((a) => <span key={a} className="contacts-muted">{a}</span>)}
      {c.company && <span className="contacts-muted">{c.company}</span>}
      {c.phone && <span className="contacts-muted">{c.phone}</span>}
      <span className="contacts-muted">{holdsText(c.holds) || 'Nothing linked yet'}{c.holds?.portal ? ' · client portal' : ''}</span>
    </label>
  );
}

// A pair is a queue item: skipped or merged, it is done; a pair that involves a contact merged away
// earlier in this review is dropped (that record no longer exists).
const pairKey = (p) => `${p.a.id}|${p.b.id}`;
export default function MergeReview({ open, pairs, onClose, onMerge }) {
  const [done, setDone] = useState(() => new Set());
  const [gone, setGone] = useState(() => new Set());
  const [keepA, setKeepA] = useState(true);
  useEffect(() => { if (open) { setDone(new Set()); setGone(new Set()); setKeepA(true); } }, [open]);
  const left = pairs.filter((p) => !done.has(pairKey(p)) && !gone.has(p.a.id) && !gone.has(p.b.id));
  const pair = left[0];
  const position = pairs.length - left.length + 1;
  useEffect(() => { setKeepA(true); }, [pair && pairKey(pair)]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  if (!pair) {
    return (
      <Modal open onClose={onClose} title="Review & merge" footer={<Button variant="secondary" onClick={onClose}>Done</Button>}>
        <p>No more possible duplicates.</p>
      </Modal>
    );
  }
  const keep = keepA ? pair.a : pair.b;
  const other = keepA ? pair.b : pair.a;
  const next = () => setDone((d) => new Set(d).add(pairKey(pair)));
  return (
    <Modal open onClose={onClose} title="Review & merge" width={640}
      footer={(
        <>
          {/* Short labels so both fit a 360px footer; the names say the whole action. */}
          <Button variant="tertiary" onClick={next} aria-label="Not the same, skip">Skip</Button>
          <Button variant="confirm" aria-label={`Merge into ${keep.name}`}
            onClick={() => { onMerge(keep, other); setGone((g) => new Set(g).add(other.id)); next(); }}>Merge</Button>
        </>
      )}>
      <p className="contacts-muted" data-testid="merge-progress">{pair.reason} · {position} of {pairs.length}</p>
      <div className="contacts-merge" role="radiogroup" aria-label="Which record to keep">
        <Side c={pair.a} chosen={keepA} onChoose={() => setKeepA(true)} />
        <Side c={pair.b} chosen={!keepA} onChoose={() => setKeepA(false)} />
      </div>
      <ul className="contacts-merge__summary" data-testid="merge-summary">
        {mergeSummary(keep, other).map((l) => <li key={l}>{l}</li>)}
      </ul>
    </Modal>
  );
}
